import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button as MuiButton,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  QuestionMark as QuestionIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  Timeline as TimelineIcon,
  CompareArrows as CompareIcon,
  Insights as InsightsIcon,
  Science as TestTubeIcon,
  Psychology as BrainIcon,
  Visibility as EyeIcon,
  TrendingDown as TrendingDownIcon,
  Speed as SpeedIcon,
  Analytics as AnalyticsIcon,
  Lightbulb as LightbulbIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ShowChart as ShowChartIcon,
  Business as BusinessIcon,
  Campaign as CampaignIcon,
  MonetizationOn as MonetizationIcon,
  AttachMoney as DollarSignIcon,
  LocationOn as MapPinIcon,
  Place as PlaceIcon,
  AccountBalance as BankIcon,
  TrendingFlat as TrendingFlatIcon,
  SavingsOutlined as SavingsIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from '@mui/icons-material';
import { snowflakeService } from '../services/snowflakeService';

interface Question {
  id?: string;                    // Generated during transformation
  title?: string;                 // Derived from 'question' field
  description?: string;           // Optional description
  category: string;               // Category from Guardrail
  question?: string;              // Original question text from Guardrail
  icon: React.ReactNode | string; // Can be React element or string name
  prompt?: string;                // Derived from 'question' field
  color?: string;                 // Color from Guardrail
  conversation_id?: string;       // UUID from backend
  status?: 'pending' | 'completed' | 'failed'; // Status of the question
  message_id?: string;            // Message ID from chat response
  profile_id?: string;            // Profile ID from chat response
  error?: string;                 // Error message when status is 'failed'
}

interface AIAnalystProps {
  clientId?: string;
  clientData?: any;
  isMaximized?: boolean;
  isActive?: boolean;
  questions?: Question[];
  questionsLoading?: boolean;
  questionsError?: string;
  workflowId?: string;
  onRefreshQuestion?: (questionId: string) => void;
  onRefreshAllQuestions?: () => void;
  onViewStateChange?: (isShowingDashboard: boolean, selectedQuestionId?: string) => void;
  /** When set, loads a specific past conversation for the given question (set by ExploreStep history panel). */
  externalConversationOverride?: { questionId: string; conversationId: string } | null;
  /** Called after the override has been consumed so ExploreStep can clear it. */
  onConversationOverrideConsumed?: () => void;
}

// Icon mapping function with color support
const getIconByName = (iconName: string, color?: string): React.ReactNode => {
  const iconStyle = color ? { color } : {};
  
  const iconComponents: Record<string, React.ComponentType> = {
    // Existing icons
    'test-tube': TestTubeIcon,
    'brain': BrainIcon,
    'eye': EyeIcon,
    'trending-up': TrendingUpIcon,
    'trending-down': TrendingDownIcon,
    'speed': SpeedIcon,
    'analytics': AnalyticsIcon,
    'assessment': AssessmentIcon,
    'search': SearchIcon,
    'timeline': TimelineIcon,
    'compare': CompareIcon,
    'insights': InsightsIcon,
    'lightbulb': LightbulbIcon,
    'bar-chart': BarChartIcon,
    'pie-chart': PieChartIcon,
    'show-chart': ShowChartIcon,
    'business': BusinessIcon,
    'campaign': CampaignIcon,
    'monetization': MonetizationIcon,
    
    // New icons for the Guardrail questions
    'chart-bar': BarChartIcon, // Alternative name for bar-chart
    'dollar-sign': DollarSignIcon,
    'map-pin': MapPinIcon,
    
    // Additional alternatives/aliases
    'money': DollarSignIcon,
    'location': MapPinIcon,
    'place': PlaceIcon,
    'savings': SavingsIcon,
    'bank': BankIcon,
    'trending-flat': TrendingFlatIcon,
  };
  
  const IconComponent = iconComponents[iconName] || QuestionIcon;
  return <IconComponent sx={iconStyle} />;
};

// Helper function to convert \n to line breaks
const formatTextWithLineBreaks = (text: string): React.ReactNode => {
  // Handle both literal \n and actual newline characters
  const lines = text.split(/\\n|\n/);
  return lines.map((line, index, array) => (
    <React.Fragment key={index}>
      {line}
      {index < array.length - 1 && <br />}
    </React.Fragment>
  ));
};

// Transform Guardrail questions to component format
const transformGuardrailQuestions = (guardrailQuestions: Question[]): Question[] => {
  return guardrailQuestions.map((gq, index) => ({
    ...gq, // Keep all original fields
    title: gq.title? gq.title : gq.question?.substring(0, 50) + '...',
    icon: typeof gq.icon === 'string' ? getIconByName(gq.icon, gq.color) : gq.icon, // Transform string icons to React elements
    prompt: gq.question, // Use question as prompt for AI
  }));
};

// Fallback questions in case Guardrail service is unavailable
const FALLBACK_QUESTIONS: Question[] = [
  {
    id: 'keyword-performance',
    title: 'Keyword Performance Analysis',
    question: 'Analyze the keyword performance data for this client. Show me the top-performing keywords, their ranking trends, and identify any significant changes or opportunities for improvement.',
    description: 'Analyze top-performing keywords and their trends over time',
    category: 'Performance',
    icon: <TrendingUpIcon />,
    prompt: 'Analyze the keyword performance data for this client. Show me the top-performing keywords, their ranking trends, and identify any significant changes or opportunities for improvement.',
  },
  {
    id: 'competitor-analysis',
    title: 'Competitor Landscape',
    question: 'Provide a comprehensive competitor analysis for this client. Compare their keyword rankings, search visibility, and market share against their main competitors.',
    description: 'Compare performance against key competitors',
    category: 'Competition',
    icon: <CompareIcon />,
    prompt: 'Provide a comprehensive competitor analysis for this client. Compare their keyword rankings, search visibility, and market share against their main competitors.',
  },
  {
    id: 'content-gaps',
    title: 'Content Gap Analysis',
    question: 'Identify content gaps and opportunities for this client. What keywords are competitors ranking for that this client is missing? What content should they create?',
    description: 'Identify content opportunities and gaps in the market',
    category: 'Content',
    icon: <SearchIcon />,
    prompt: 'Identify content gaps and opportunities for this client. What keywords are competitors ranking for that this client is missing? What content should they create?',
  },
];

const AIAnalyst: React.FC<AIAnalystProps> = ({
  clientId,
  clientData,
  isMaximized = false,
  isActive = false,
  questions = [],
  questionsLoading = false,
  questionsError = '',
  workflowId,
  onRefreshQuestion,
  onRefreshAllQuestions,
  onViewStateChange,
  externalConversationOverride,
  onConversationOverrideConsumed,
}) => {
  const theme = useTheme();
  const [iframeUrl, setIframeUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [showQuestionDashboard, setShowQuestionDashboard] = useState(true);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailQuestion, setDetailQuestion] = useState<Question | null>(null);
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const previousConversationIdRef = useRef<string | undefined>(undefined);
  const createdConversationsRef = useRef<Set<string>>(new Set());

  // Helper method to build Guardrail iframe URL with common parameters
  const buildGuardrailChatUrl = (
    baseUrl: string,
    conversationId: string | undefined,
    token: string,
    profileId?: string,
    initialMessage?: string
  ): string => {
    // Build base URL with or without conversation_id
    let url = conversationId
      ? `${baseUrl}/embed/chat/${conversationId}?theme=light&token=${token}`
      : `${baseUrl}/embed/chat?theme=light&token=${token}`;

    // Add profile_id if provided (from profile_cortex_agent)
    if (profileId) {
      url += `&profile_id=${encodeURIComponent(profileId)}`;
    }

    // Add client_id if available
    if (clientId) {
      url += `&client_id=${encodeURIComponent(clientId)}`;
    }

    // Add client_code if available
    if (clientData?.client_code) {
      url += `&client_code=${encodeURIComponent(clientData.client_code)}`;
    }

    // Add client domains if available
    if (clientData?.domains) {
      url += `&client_domains=${encodeURIComponent(clientData.domains)}`;
    }

    // Add client name if available
    if (clientData?.name) {
      url += `&client_name=${encodeURIComponent(clientData.name)}`;
    }

    // Add workflow_id if available
    if (workflowId) {
      url += `&workflow_id=${encodeURIComponent(workflowId)}`;
    }

    // Add initial message if provided
    if (initialMessage) {
      url += `&initial_message=${encodeURIComponent(initialMessage)}`;
    }

    return url;
  };

  // When ExploreStep selects a history conversation from the toolbar popover, load it here
  useEffect(() => {
    if (!externalConversationOverride) return;
    const { questionId, conversationId } = externalConversationOverride;
    const question = questions.find((q) => q.id === questionId);
    if (question) {
      handleQuestionChange({ ...question, conversation_id: conversationId });
    }
    onConversationOverrideConsumed?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalConversationOverride]);

  // Update iframe height on window resize
  useEffect(() => {
    const updateIframeHeight = () => {
      if (iframeRef.current) {
        const viewportHeight = window.innerHeight;
        const newHeight = Math.max(600, viewportHeight - 200);
        iframeRef.current.style.height = `${newHeight}px`;
      }
    };

    // Initial height calculation
    updateIframeHeight();

    window.addEventListener('resize', updateIframeHeight);
    return () => window.removeEventListener('resize', updateIframeHeight);
  }, [iframeUrl]);

  // Notify parent of view state changes
  useEffect(() => {
    if (onViewStateChange) {
      onViewStateChange(showQuestionDashboard, selectedQuestion?.id);
    }
  }, [showQuestionDashboard, selectedQuestion, onViewStateChange]);

  // Ensure a Guardrail conversation exists under the user's own authguard token.
  // Mirrors the Free Chat pattern - skips if already registered this session.
  const ensureGuardrailConversation = async (conversationId: string): Promise<void> => {
    if (createdConversationsRef.current.has(conversationId)) return;
    try {
      await snowflakeService.createGuardrailConversationWithUserToken(conversationId);
      console.log('AI Analyst: Guardrail conversation registered with user token:', conversationId);
    } catch (err) {
      console.warn('AI Analyst: Failed to register conversation with user token (continuing):', err);
    }
    createdConversationsRef.current.add(conversationId);
  };

  // Watch for conversation_id changes in the selected question and reload iframe
  useEffect(() => {
    const reloadIframeWithNewConversation = async (question: Question) => {
      // Only reload if we have a valid conversation_id (not 'pending' or 'failed')
      const isValidConversationId = question.conversation_id && 
                                     question.conversation_id !== 'pending' && 
                                     question.conversation_id !== 'failed';
      
      if (!isValidConversationId) {
        console.log('Skipping iframe load - conversation_id is not valid yet:', question.conversation_id);
        return;
      }

      try {
        console.log('Loading iframe with conversation_id:', question.conversation_id);
        const [sessionData] = await Promise.all([
          snowflakeService.getGuardrailChatConfig(),
          ensureGuardrailConversation(question.conversation_id!),
        ]);
        
        const url = buildGuardrailChatUrl(
          sessionData.base_url,
          question.conversation_id,
          sessionData.token,
          sessionData.profile_id
        );

        setIframeUrl(url);
        setSelectedQuestion(question);
      } catch (err) {
        console.error('Failed to reload iframe with new conversation:', err);
      }
    };

    if (selectedQuestion && !showQuestionDashboard) {
      // Find the latest question data from the questions array
      const latestQuestion = questions.find(q => q.id === selectedQuestion.id);
      
      if (latestQuestion && latestQuestion.conversation_id) {
        const currentConversationId = latestQuestion.conversation_id;
        
        // Skip if the conversation_id is 'pending' or 'failed' - wait for polling to update it
        const isValidConversationId = currentConversationId !== 'pending' && currentConversationId !== 'failed';
        
        // Check if conversation_id changed from previous value
        if (previousConversationIdRef.current && 
            previousConversationIdRef.current !== currentConversationId &&
            isValidConversationId) {
          console.log('Conversation ID changed from', previousConversationIdRef.current, 'to', currentConversationId);
          console.log('Reloading iframe with new conversation');
          reloadIframeWithNewConversation(latestQuestion);
        }
        
        // Update the ref with current conversation_id only if it's valid
        // This ensures we'll detect the change when it goes from 'pending' to a real UUID
        if (isValidConversationId) {
          previousConversationIdRef.current = currentConversationId;
        }
      }
    }
  }, [questions, selectedQuestion, showQuestionDashboard, clientId, clientData, workflowId]);

  // Automatically load AI Analyst when component becomes active
  useEffect(() => {
    if (isActive && !iframeUrl && !loading && !error) {
      loadAIAnalyst();
    }
  }, [isActive, iframeUrl, loading, clientData, error]);

  const loadAIAnalyst = async (question?: Question) => {
    try {
      setLoading(true);
      setError('');

      // Check if question has a valid conversation_id (not 'pending' or 'failed')
      const hasValidConversationId = question?.conversation_id && 
                                      question.conversation_id !== 'pending' && 
                                      question.conversation_id !== 'failed';

      // Don't load iframe if conversation is pending - the loading UI will show instead
      if (question && !hasValidConversationId && question.status === 'pending') {
        console.log('Question is pending, showing loading state instead of iframe');
        setLoading(false);
        return;
      }

      const conversationId = hasValidConversationId ? question!.conversation_id : undefined;

      const [sessionData] = await Promise.all([
        snowflakeService.getGuardrailChatConfig(),
        conversationId ? ensureGuardrailConversation(conversationId) : Promise.resolve(),
      ]);

      // Build URL with or without conversation_id and initial message
      const url = buildGuardrailChatUrl(
        sessionData.base_url,
        conversationId,
        sessionData.token,
        sessionData.profile_id,
        question?.prompt && !hasValidConversationId ? question.prompt : undefined
      );

      setIframeUrl(url);
    } catch (err) {
      console.error('Failed to create session:', err);
      setError('Failed to load AI Analyst. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle question selection
  const handleQuestionSelect = (question: Question) => {
    setSelectedQuestion(question);
    setShowQuestionDashboard(false);
    loadAIAnalyst(question);
  };

  // Handle question change from navigation panel (only updates iframe, no full reload)
  const handleQuestionChange = async (question: Question) => {
    setSelectedQuestion(question);
    
    // Check if question has a valid conversation_id (not 'pending' or 'failed')
    const hasValidConversationId = question?.conversation_id && 
                                    question.conversation_id !== 'pending' && 
                                    question.conversation_id !== 'failed';

    // Don't load iframe if conversation is pending - the loading UI will show instead
    if (!hasValidConversationId && question.status === 'pending') {
      console.log('Question is pending, showing loading state instead of iframe');
      return;
    }
    
    // Build new URL directly without triggering loading state
    try {
      const conversationId = hasValidConversationId ? question.conversation_id : undefined;

      const [sessionData] = await Promise.all([
        snowflakeService.getGuardrailChatConfig(),
        conversationId ? ensureGuardrailConversation(conversationId) : Promise.resolve(),
      ]);
      
      const url = buildGuardrailChatUrl(
        sessionData.base_url,
        conversationId,
        sessionData.token,
        sessionData.profile_id,
        question?.prompt && !hasValidConversationId ? question.prompt : undefined
      );

      // Directly update iframe URL without clearing it first
      setIframeUrl(url);
    } catch (err) {
      console.error('Failed to switch question:', err);
      setError('Failed to load question. Please try again.');
    }
  };

  const handleDetailDialogOpen = (question: Question, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    setDetailQuestion(question);
    setDetailDialogOpen(true);
  };

  const handleDetailDialogClose = () => {
    setDetailDialogOpen(false);
    setDetailQuestion(null);
  };

  // Transform questions for display (apply icon transformation and fallback)
  const displayQuestions = questions.length > 0 
    ? transformGuardrailQuestions(questions)
    : FALLBACK_QUESTIONS;

  const iframeStyle = {
    border: 'none',
    display: 'flex',
    flex: 1,
    height: '100%',
    minHeight: isMaximized ? 0 : '500px',
    margin: 0,
    padding: 0
  };

  const tabBoxStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1
  };

  // Show question dashboard if no question is selected or if explicitly showing
  if (showQuestionDashboard || (!iframeUrl && !loading && !error && !selectedQuestion)) {
    return (
      <Box sx={{ 
        flex: 1, 
        p: isMaximized ? 2 : 3,
        overflowY: 'auto'
      }}>
        <Box sx={{ mb: 4, textAlign: 'center', position: 'relative' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            AI Analyst
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Choose a question to start your analysis. Our AI will provide insights based on your client's SERP data.
          </Typography>
          
          {/* Refresh All Button - Absolute positioned in top right */}
          {onRefreshAllQuestions && displayQuestions.length > 0 && (
            <MuiButton
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={onRefreshAllQuestions}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                textTransform: 'none',
                borderColor: 'action.active',
                color: 'action.active',
                '&:hover': {
                  borderColor: 'error.main',
                  backgroundColor: 'warning.light',
                  color: 'error.main',
                  '& .MuiSvgIcon-root': {
                    transform: 'rotate(180deg)',
                    transition: 'transform 0.3s ease-in-out',
                  }
                }
              }}
            >
              Refresh All
            </MuiButton>
          )}
        </Box>

        {questionsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <CircularProgress size={24} sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Loading questions from Guardrail...
            </Typography>
          </Box>
        )}

        {questionsError && (
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
              ⚠️ Using fallback questions: {questionsError}
            </Typography>
          </Box>
        )}


        <Grid container spacing={3} sx={{ maxWidth: 1200, mx: 'auto' }}>
          {displayQuestions.map((question) => (
            <Grid item xs={12} sm={6} md={4} key={question.id}>
              <Card
                sx={{
                  height: '100%',
                  cursor: question.status === 'completed' ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease-in-out',
                  opacity: question.status === 'completed' ? 1 : question.status === 'failed' ? 0.6 : 0.7,
                  position: 'relative',
                  ...(question.status === 'failed' && {
                    borderColor: 'error.light',
                    borderWidth: 1,
                    borderStyle: 'solid',
                  }),
                  '&:hover': question.status === 'completed' ? {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  } : {},
                }}
                onClick={() => question.status === 'completed' && handleQuestionSelect(question)}
              >
                <CardContent sx={{ p: 2, height: 'calc(100% - 20px)', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {question.icon}
                    </Box>
                    <Chip 
                      label={question.category} 
                      size="small" 
                      variant="outlined"
                      sx={{ 
                        ml: 'auto',
                        mr: 1,
                        ...(question.color && {
                          color: question.color,
                          borderColor: question.color,
                          '&:hover': {
                            backgroundColor: `${question.color}10`, // 10% opacity background on hover
                          }
                        })
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => handleDetailDialogOpen(question, e)}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'primary.main',
                          backgroundColor: 'primary.main',
                          '& .MuiSvgIcon-root': {
                            color: 'white',
                          }
                        }
                      }}
                    >
                      <InfoIcon fontSize="small" />
                    </IconButton>
                    {question.id && onRefreshQuestion && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRefreshQuestion(question.id!);
                        }}
                        sx={{
                          color: question.status === 'failed' ? 'error.main' : 'action.active',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            color: 'error.main',
                            backgroundColor: 'warning.light',
                            transform: 'rotate(90deg)',
                          }
                        }}
                        title={question.status === 'failed' ? "Retry - Start new analysis" : "Refresh - Start new analysis"}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {question.title ? formatTextWithLineBreaks(question.title) : question.title}
                    </Typography>
                    
                    {/* Content area */}
                    {question.description && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ lineHeight: 1.5, flex: 1 }}
                      >
                        {formatTextWithLineBreaks(question.description)}
                      </Typography>
                    )}
                  </Box>
                  
                  {/* Conversation ID Footer (for completed status) */}
                  {question.status === 'completed' && question.conversation_id && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'flex-end',
                      gap: 0.5,
                      mt: 1
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {question.error && (
                          <WarningIcon 
                            sx={{ 
                              fontSize: '0.9rem', 
                              color: 'warning.main',
                            }} 
                            titleAccess={question.error}
                          />
                        )}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            color: question.error ? 'warning.main' : 'text.disabled',
                          }}
                        >
                          {question.conversation_id}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Status Messages */}
                  {question.status === 'failed' && (
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      mt: 1,
                      py: 0.5,
                      gap: 1
                    }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'error.main',
                          fontWeight: 500,
                          opacity: 0.8
                        }}
                      >
                        ⚠️ {question.error || 'Failed to process this question'}
                      </Typography>
                    </Box>
                  )}
                  
                  {question.status === 'pending' && (
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        mt: 1,
                        py: 0.5,
                        gap: 1
                      }}
                    >
                      <CircularProgress 
                        size={16} 
                        sx={{ 
                          color: question.color || 'warning.main'
                        }} 
                      />
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'warning.main',
                          fontWeight: 500,
                          opacity: 0.8
                        }}
                      >
                        Processing question...
                      </Typography>
                    </Box>
                  )}
                </CardContent>

              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Question Detail Dialog - Using Modal instead */}
        {detailDialogOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
            onClick={handleDetailDialogClose}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                maxWidth: 600,
                width: '90%',
                maxHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 24,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Fixed Header */}
              <Box sx={{ p: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {detailQuestion?.icon}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" component="div">
                      {detailQuestion?.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {detailQuestion?.category}
                    </Typography>
                    {detailQuestion?.id && (
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontFamily: 'monospace' }}>
                        ID: {detailQuestion.id}
                      </Typography>
                    )}
                  </Box>
                  <IconButton onClick={handleDetailDialogClose} size="small">
                    ✕
                  </IconButton>
                </Box>
              </Box>
              
              {/* Scrollable Content */}
              <Box sx={{ 
                flex: 1, 
                overflow: 'auto', 
                p: 3, 
                py: 2,
                minHeight: 0 // Important for flex child to be scrollable
              }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {detailQuestion?.prompt || 'No detailed prompt available for this question.'}
                </Typography>
              </Box>
              
              {/* Fixed Footer */}
              <Box sx={{ p: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <MuiButton onClick={handleDetailDialogClose} variant="contained">
                    Close
                  </MuiButton>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  // Show loading/error states
  if (!iframeUrl) {
    return (
      <Box sx={{ ...tabBoxStyle, color: 'text.secondary' }}>
        {loading ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            Loading AI Analyst...
          </>
        ) : error ? (
          <>
            <Box sx={{ color: 'error.main', mb: 2 }}>
              {error}
            </Box>
            <MuiButton variant="contained" onClick={() => loadAIAnalyst(selectedQuestion || undefined)}>
              Try again
            </MuiButton>
          </>
        ) : (
          'Initializing AI Analyst...'
        )}
      </Box>
    );
  }

  // Show AI Analyst iframe with left navigation panel
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', minHeight: 0 }}>
      {/* Left Navigation Panel */}
      <Box
        sx={{
          width: isNavCollapsed ? 48 : 280,
          minWidth: isNavCollapsed ? 48 : 280,
          borderRight: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'grey.50',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s ease-in-out, min-width 0.2s ease-in-out',
          overflow: 'hidden',
        }}
      >
        {/* Navigation Header */}
        <Box
          sx={{
            px: isNavCollapsed ? 1 : 1.5,
            py: '5.5px',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isNavCollapsed ? 'center' : 'space-between',
            minHeight: 48,
          }}
        >
          {!isNavCollapsed && (
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Questions
            </Typography>
          )}
          <IconButton
            size="small"
            onClick={() => setIsNavCollapsed(!isNavCollapsed)}
            sx={{ p: 0.5 }}
            title={isNavCollapsed ? 'Expand panel' : 'Collapse panel'}
          >
            {isNavCollapsed ? <KeyboardArrowRightIcon fontSize="small" /> : <KeyboardArrowLeftIcon fontSize="small" />}
          </IconButton>
        </Box>

        {/* Back to Cards Button */}
        {!isNavCollapsed && (
          <Box sx={{ p: 1.5, pt: 1 }}>
            <MuiButton
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => {
                setShowQuestionDashboard(true);
                setSelectedQuestion(null);
                setIframeUrl('');
              }}
              sx={{ 
                textTransform: 'none',
                fontSize: '0.75rem',
                py: 0.5,
              }}
            >
              ← Back to Cards
            </MuiButton>
          </Box>
        )}

        {/* Question List */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <List dense sx={{ py: 0.5 }}>
            {displayQuestions.map((question) => (
              <ListItem key={question.id} disablePadding sx={{ px: isNavCollapsed ? 0.5 : 1 }}>
                {isNavCollapsed ? (
                  <IconButton
                    onClick={() => question.status === 'completed' && handleQuestionChange(question)}
                    disabled={question.status !== 'completed'}
                    sx={{
                      width: 36,
                      height: 36,
                      mx: 'auto',
                      my: 0.25,
                      backgroundColor: selectedQuestion?.id === question.id ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                      opacity: question.status === 'completed' ? 1 : 0.5,
                      '&:hover': {
                        backgroundColor: selectedQuestion?.id === question.id ? 'rgba(25, 118, 210, 0.12)' : 'grey.200',
                      },
                    }}
                    title={question.title}
                  >
                    {question.status === 'pending' ? (
                      <CircularProgress size={16} sx={{ color: question.color || 'warning.main' }} />
                    ) : (
                      question.icon
                    )}
                  </IconButton>
                ) : (
                  <ListItemButton
                    onClick={() => question.status === 'completed' && handleQuestionChange(question)}
                    selected={selectedQuestion?.id === question.id}
                    disabled={question.status !== 'completed'}
                    sx={{
                      borderRadius: 1,
                      py: 1,
                      px: 1.5,
                      mb: 0.5,
                      opacity: question.status === 'completed' ? 1 : 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.12)',
                        },
                      },
                    }}
                  >
                    <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      {question.status === 'pending' ? (
                        <CircularProgress size={20} sx={{ color: question.color || 'warning.main' }} />
                      ) : (
                        question.icon
                      )}
                    </Box>
                    <ListItemText
                      primary={question.title}
                      secondary={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            label={question.category}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              color: 'text.secondary',
                              borderColor: 'grey.300',
                              backgroundColor: 'grey.100',
                            }}
                            variant="outlined"
                          />
                          {question.status === 'failed' && (
                            <WarningIcon sx={{ fontSize: 14, color: 'error.main' }} titleAccess={question.error} />
                          )}
                        </Box>
                      }
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: selectedQuestion?.id === question.id ? 600 : 500,
                        color: 'text.primary',
                        sx: {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.8rem',
                        },
                      }}
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItemButton>
                )}
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>

      {/* AI Analyst Iframe */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {(() => {
          // Get the latest question data from questions array
          const latestQuestion = selectedQuestion 
            ? questions.find(q => q.id === selectedQuestion.id) || selectedQuestion
            : null;
          
          const isPending = latestQuestion && 
                          (latestQuestion.status === 'pending' || 
                           latestQuestion.conversation_id === 'pending');
          
          return isPending ? (
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              color: 'text.secondary',
              gap: 2
            }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Processing your question...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This may take a few moments
              </Typography>
            </Box>
          ) : (
            <iframe
              ref={iframeRef}
              src={iframeUrl}
              width="100%"
              allow="clipboard-write"
              style={iframeStyle}
              title="AI Analyst"
            />
          );
        })()}
      </Box>
    </Box>
  );
};

export default AIAnalyst;
